import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { Loader2, X } from "lucide-react";
import { useAuth } from "../../../supabase/auth";
import { supabase } from "../../../supabase/supabase";
import { useOrganization } from "../../App";
import MultiSelect from "./MultiSelect";

type Persona = {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  persona_prompt: string;
  avg_words_per_response: number;
  behavior_attributes: string[];
  speaking_style: string;
  interaction_style: string;
  technical_level: string;
  additional_config: any;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string | null;
};

interface PersonaFormProps {
  persona: Persona | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const BEHAVIOR_ATTRIBUTES = [
  // Personality traits
  "Polite", "Aggressive", "Impatient", "Patient", "Friendly", "Hostile", 
  "Curious", "Dismissive", "Cooperative", "Uncooperative", "Skeptical", 
  "Trusting", "Confident", "Worried", "Calm", "Stressed", "Excited",
  
  // Communication styles
  "Talkative", "Concise", "Detailed", "Vague", "Clear", "Repetitive",
  
  // Age-related
  "Elderly", "Young", "Middle-aged",
  
  // Technical aptitude (will be separate from technical_level field)
  "Tech-savvy", "Tech-challenged", "Digital-native"
];

const SPEAKING_STYLES = [
  { value: "formal", label: "Formal" },
  { value: "casual", label: "Casual" },
  { value: "professional", label: "Professional" },
  { value: "technical", label: "Technical" },
];

const INTERACTION_STYLES = [
  { value: "cooperative", label: "Cooperative" },
  { value: "hostile", label: "Hostile" },
  { value: "curious", label: "Curious" },
  { value: "dismissive", label: "Dismissive" },
  { value: "engaged", label: "Engaged" },
];

const TECHNICAL_LEVELS = [
  { value: "beginner", label: "Beginner" },
  { value: "intermediate", label: "Intermediate" },
  { value: "expert", label: "Expert" },
];

export default function PersonaForm({ persona, onSuccess, onCancel }: PersonaFormProps) {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    persona_prompt: "",
    avg_words_per_response: 20,
    behavior_attributes: [] as string[],
    speaking_style: "casual",
    interaction_style: "cooperative",
    technical_level: "intermediate",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (persona) {
      setFormData({
        name: persona.name,
        description: persona.description || "",
        persona_prompt: persona.persona_prompt,
        avg_words_per_response: persona.avg_words_per_response,
        behavior_attributes: persona.behavior_attributes || [],
        speaking_style: persona.speaking_style,
        interaction_style: persona.interaction_style,
        technical_level: persona.technical_level,
      });
    } else {
      // Reset form for new persona
      setFormData({
        name: "",
        description: "",
        persona_prompt: "",
        avg_words_per_response: 20,
        behavior_attributes: [],
        speaking_style: "casual",
        interaction_style: "cooperative",
        technical_level: "intermediate",
      });
    }
    setErrors({});
  }, [persona]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.persona_prompt.trim()) {
      newErrors.persona_prompt = "Persona prompt is required";
    }

    if (formData.avg_words_per_response < 1 || formData.avg_words_per_response > 500) {
      newErrors.avg_words_per_response = "Average words must be between 1 and 500";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!currentOrganization || !user) {
      toast({
        title: "Error",
        description: "User authentication or organization required",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const now = new Date().toISOString();
      const personaData = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        persona_prompt: formData.persona_prompt.trim(),
        avg_words_per_response: formData.avg_words_per_response,
        behavior_attributes: formData.behavior_attributes,
        speaking_style: formData.speaking_style,
        interaction_style: formData.interaction_style,
        technical_level: formData.technical_level,
        additional_config: {},
        organization_id: currentOrganization.id,
        updated_at: now,
        updated_by: user.id,
      };

      if (persona) {
        // Update existing persona
        const { error } = await supabase
          .from("personas")
          .update(personaData)
          .eq("id", persona.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Persona updated successfully",
        });
      } else {
        // Create new persona
        const { error } = await supabase.from("personas").insert({
          ...personaData,
          created_by: user.id,
          created_at: now,
        });

        if (error) throw error;

        toast({
          title: "Success",
          description: "Persona created successfully",
        });
      }

      onSuccess();
    } catch (error: any) {
      console.error("Error saving persona:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save persona",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const generateSamplePrompt = () => {
    const attributes = formData.behavior_attributes.join(", ");
    const samplePrompt = `You are a ${formData.technical_level} user who communicates in a ${formData.speaking_style} style and has a ${formData.interaction_style} interaction approach. Your behavioral characteristics include: ${attributes || "standard user behaviors"}. 

Keep your responses around ${formData.avg_words_per_response} words on average. ${formData.description ? `Context: ${formData.description}` : ""}

Respond naturally as this persona would, maintaining consistency with these traits throughout the conversation.`;

    setFormData({
      ...formData,
      persona_prompt: samplePrompt,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Basic Information</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Impatient Customer"
              className={errors.name ? "border-destructive" : ""}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="avg_words_per_response">Average Words per Response *</Label>
            <Input
              id="avg_words_per_response"
              type="number"
              min="1"
              max="500"
              value={formData.avg_words_per_response}
              onChange={(e) => setFormData({ 
                ...formData, 
                avg_words_per_response: parseInt(e.target.value) || 20 
              })}
              className={errors.avg_words_per_response ? "border-destructive" : ""}
            />
            {errors.avg_words_per_response && (
              <p className="text-sm text-destructive">{errors.avg_words_per_response}</p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Describe this persona's background, context, and key characteristics..."
            rows={3}
          />
        </div>
      </div>

      {/* Behavioral Configuration */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Behavioral Configuration</h3>
        
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="speaking_style">Speaking Style</Label>
            <Select
              value={formData.speaking_style}
              onValueChange={(value) => setFormData({ ...formData, speaking_style: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SPEAKING_STYLES.map((style) => (
                  <SelectItem key={style.value} value={style.value}>
                    {style.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="interaction_style">Interaction Style</Label>
            <Select
              value={formData.interaction_style}
              onValueChange={(value) => setFormData({ ...formData, interaction_style: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INTERACTION_STYLES.map((style) => (
                  <SelectItem key={style.value} value={style.value}>
                    {style.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="technical_level">Technical Level</Label>
            <Select
              value={formData.technical_level}
              onValueChange={(value) => setFormData({ ...formData, technical_level: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TECHNICAL_LEVELS.map((level) => (
                  <SelectItem key={level.value} value={level.value}>
                    {level.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="behavior_attributes">Behavioral Attributes</Label>
          <MultiSelect
            options={BEHAVIOR_ATTRIBUTES}
            selected={formData.behavior_attributes}
            onChange={(selected) => setFormData({ ...formData, behavior_attributes: selected })}
            placeholder="Select behavioral characteristics..."
          />
        </div>
      </div>

      {/* Persona Prompt */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Persona Prompt</h3>
          <Button
            type="button"
            variant="outline"
            onClick={generateSamplePrompt}
            disabled={isSubmitting}
          >
            Generate Sample Prompt
          </Button>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="persona_prompt">System Prompt *</Label>
          <Textarea
            id="persona_prompt"
            value={formData.persona_prompt}
            onChange={(e) => setFormData({ ...formData, persona_prompt: e.target.value })}
            placeholder="Enter the system prompt that defines how this persona should behave..."
            rows={8}
            className={errors.persona_prompt ? "border-destructive" : ""}
          />
          {errors.persona_prompt && (
            <p className="text-sm text-destructive">{errors.persona_prompt}</p>
          )}
          <p className="text-sm text-muted-foreground">
            This prompt will be used to instruct the AI on how to simulate this persona's behavior and responses.
          </p>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-2 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {persona ? "Updating..." : "Creating..."}
            </>
          ) : (
            persona ? "Update Persona" : "Create Persona"
          )}
        </Button>
      </div>
    </form>
  );
}